import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Plus, 
  Download, 
  Upload,
  Building2,
  Wallet,
  ArrowUpRight,
  Award,
  TrendingUp,
  FileText
} from 'lucide-react'
import { motion } from 'framer-motion'
import { 
  LineChart, Line, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area
} from 'recharts'
import { portfolioTrackerService } from '@/services/portfolioTrackerService'
import { 
  PortfolioTrackerSummary,
  PortfolioGraphData
} from '@/types/portfolio'
import { formatMoney, formatPercentage, formatDate } from '@/utils'
import { PageTemplate } from '@/components/templates'
import { Button, Input } from '@/components/atoms'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CONTAINER_SIZES } from '@/constants'
import { AddAssetModal, EditAssetModal } from '@/components/organisms/modals'

// Chart colors matching Figma design
const CHART_COLORS = {
  portfolioValue: '#a9c804',  // Green
  totalInvested: '#ff8e00',   // Orange
  profitLoss: '#2d2923',      // Dark
  bitcoin: '#a9c804',         // Green
  ethereum: '#3fc8e4',        // Blue
  solana: '#ee9d1a',          // Orange
}

// Array of colors for stacked graph
const STACKED_COLORS = ['#a9c804', '#3fc8e4', '#ee9d1a', '#ff8042', '#0088fe']

interface MetricCardProps {
  icon: 'invested' | 'current' | 'roi' | 'profit' | 'assets'
  label: string
  value: string
  valuePrefix?: string
  valueSuffix?: string
}

const MetricCard = ({ icon, label, value, valuePrefix, valueSuffix }: MetricCardProps) => {
  const iconMap = {
    invested: Wallet,
    current: ArrowUpRight,
    roi: Award,
    profit: TrendingUp,
    assets: FileText,
  }
  const Icon = iconMap[icon]

  return (
    <div className="relative bg-white dark:bg-[#121212] h-[124px] overflow-hidden flex-1 min-w-0">
      <div className="absolute flex flex-col h-[76px] items-start justify-between left-[24px] top-[24px]">
        {/* Header with icon and label */}
        <div className="flex gap-[6px] items-center w-full">
          <div className="shrink-0 size-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            <Icon className="w-full h-full" strokeWidth={1.5} />
          </div>
          <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            {label}
          </p>
        </div>

        {/* Value */}
        <div className="flex gap-[4px] items-center w-full">
          <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[32px] text-[23px] tracking-[-0.2px]">
            {valuePrefix && (
              <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">{valuePrefix}</span>
            )}
            <span className="text-[#2d2923] dark:text-[#fdfdfd]">{value}</span>
            {valueSuffix && (
              <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)] text-[15px]">{valueSuffix}</span>
            )}
          </p>
        </div>
      </div>
    </div>
  )
}

interface AssetCardProps {
  name: string
  date: string
  investedAmount: string
  valuation: string
  terms: string
  onViewDetails: () => void
  onDelete: () => void
}

const AssetCard = ({ name, date, investedAmount, valuation, terms, onViewDetails, onDelete }: AssetCardProps) => {
  return (
    <div className="bg-white dark:bg-[#121212] flex flex-col gap-[18px] items-start p-[20px] w-full">
      {/* Header with icon and name */}
      <div className="flex gap-[10px] items-center w-full">
        <div className="shrink-0 size-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
          <FileText className="w-full h-full" strokeWidth={1.5} />
        </div>
        <p className="font-['Plus_Jakarta_Sans'] font-semibold leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-[16px]">
          {name}
        </p>
      </div>

      {/* Info row */}
      <div className="flex gap-[18px] items-start w-full">
        <div className="flex flex-col gap-[4px] items-start flex-1 min-w-0">
          <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            Date
          </p>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
            {date}
          </p>
        </div>
        <div className="flex flex-col gap-[4px] items-start flex-1 min-w-0">
          <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            Invested Amount
          </p>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
            {investedAmount}
          </p>
        </div>
        <div className="flex flex-col gap-[4px] items-start flex-1 min-w-0">
          <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            Valuation
          </p>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
            {valuation}
          </p>
        </div>
        <div className="flex flex-col gap-[4px] items-start flex-1 min-w-0">
          <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            Terms
          </p>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
            {terms}
          </p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-[18px] items-start w-full">
        <button
          onClick={onViewDetails}
          className="flex-1 bg-white dark:bg-[#121212] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] flex items-center justify-center px-[16px] py-[8px] font-['Plus_Jakarta_Sans'] font-medium leading-[16px] text-[#2d2923] dark:text-[#fdfdfd] text-[12px] hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
        >
          View Details
        </button>
        <button
          onClick={onDelete}
          className="bg-white dark:bg-[#121212] border border-[#e5907f] flex items-center justify-center px-[24px] py-[8px] font-['Plus_Jakarta_Sans'] font-medium leading-[16px] text-[#ff540c] text-[12px] hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
        >
          Delete
        </button>
      </div>
    </div>
  )
}

const PortfolioDetailScreen = () => {
  const { portfolioId } = useParams<{ portfolioId: string }>()
  const navigate = useNavigate()
  
  const [portfolio, setPortfolio] = useState<PortfolioTrackerSummary | null>(null)
  const [portfolioInfo, setPortfolioInfo] = useState<{ name: string } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showAddAssetModal, setShowAddAssetModal] = useState(false)
  const [showEditAssetModal, setShowEditAssetModal] = useState(false)
  const [editingAsset, setEditingAsset] = useState<PortfolioTrackerSummary['assets'][0] | null>(null)
  const [editingName, setEditingName] = useState('')
  const [deleting, setDeleting] = useState(false)
  
  // Chart data state
  const [chartData, setChartData] = useState<PortfolioGraphData['monthly_values']>([])
  const [assetNames, setAssetNames] = useState<Record<string, string>>({})
  const [chartLoading, setChartLoading] = useState(true)

  useEffect(() => {
    if (portfolioId) {
      loadData()
      loadChartData()
    }
  }, [portfolioId])

  const loadData = async () => {
    if (!portfolioId) return

    try {
      setLoading(true)
      setError(null)
      
      const [portfolioData, portfoliosList] = await Promise.all([
        portfolioTrackerService.getPortfolioSummary(portfolioId),
        portfolioTrackerService.getPortfolios()
      ])
      
      setPortfolio(portfolioData)
      
      // Find portfolio name from the list
      const portfolioFromList = portfoliosList.data?.find(p => p.portfolio_id === portfolioId)
      if (portfolioFromList) {
        setPortfolioInfo({ name: portfolioFromList.name })
        setEditingName(portfolioFromList.name)
      }
    } catch (err) {
      setError('Failed to load portfolio data')
      console.error('Error loading portfolio data:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadChartData = async () => {
    if (!portfolioId) return

    try {
      setChartLoading(true)
      const response = await portfolioTrackerService.getPortfolioGraph(portfolioId)
      
      if (response.monthly_values && response.monthly_values.length > 0) {
        setChartData(response.monthly_values)
        
        // Extract asset names from graph data points
        const assetNamesMap: Record<string, string> = {}
        
        response.monthly_values.forEach(dataPoint => {
          if (dataPoint.assets && dataPoint.assets.length > 0) {
            dataPoint.assets.forEach(asset => {
              if (asset.asset_id && !assetNamesMap[asset.asset_id] && asset.name) {
                assetNamesMap[asset.asset_id] = asset.name
              }
            })
          }
        })
        
        setAssetNames(assetNamesMap)
      }
    } catch (err) {
      console.error('Error loading chart data:', err)
    } finally {
      setChartLoading(false)
    }
  }

  const handleEditPortfolio = async () => {
    if (!portfolioId || !editingName.trim()) return

    try {
      await portfolioTrackerService.updatePortfolio(portfolioId, editingName.trim())
      setPortfolio(prev => prev ? { ...prev, name: editingName.trim() } : null)
      setShowEditModal(false)
    } catch (err) {
      setError('Failed to update portfolio')
      console.error('Error updating portfolio:', err)
    }
  }

  const handleDeletePortfolio = async () => {
    if (!portfolioId) return

    try {
      setDeleting(true)
      await portfolioTrackerService.deletePortfolio(portfolioId)
      navigate('/portfolio')
    } catch (err) {
      setError('Failed to delete portfolio')
      console.error('Error deleting portfolio:', err)
      setDeleting(false)
    }
  }

  const handleEditAsset = (asset: PortfolioTrackerSummary['assets'][0]) => {
    setEditingAsset(asset)
    setShowEditAssetModal(true)
  }

  // Suppress unused warning - function is used in asset cards
  void handleEditAsset

  const handleDeleteAsset = async (assetId: string) => {
    if (!portfolioId) return
    
    if (window.confirm('Are you sure you want to delete this asset? This action cannot be undone.')) {
      try {
        await portfolioTrackerService.deleteAsset(assetId)
        loadData()
        loadChartData()
      } catch (err) {
        setError('Failed to delete asset')
        console.error('Error deleting asset:', err)
      }
    }
  }

  const handleExportPortfolio = () => {
    if (!portfolio) return

    const csvData = [
      ['Project Name', 'Invested Amount', 'Current Value', 'Gain/Loss USD', 'Gain/Loss %', 'Valuation Source'],
      ...portfolio.assets.map(asset => [
        asset.project.name,
        formatMoney(asset.invested_amount),
        formatMoney(asset.current_value),
        formatMoney(asset.gain_loss_usd),
        formatPercentage(asset.gain_loss_percentage),
        asset.valuation_source
      ])
    ]

    const csvContent = csvData.map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${portfolioInfo?.name || 'portfolio'}_export.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const getUniqueAssetIds = () => {
    const assetIds = new Set<string>()
    chartData.forEach(dataPoint => {
      if (dataPoint.assets && dataPoint.assets.length > 0) {
        dataPoint.assets.forEach(asset => {
          assetIds.add(asset.asset_id)
        })
      }
    })
    return Array.from(assetIds)
  }

  // Format large numbers like in Figma
  const formatCompactNumber = (num: number): string => {
    if (num >= 1e9) {
      return `${(num / 1e9).toFixed(1)}B`
    } else if (num >= 1e6) {
      return `${(num / 1e6).toFixed(1)}M`
    } else if (num >= 1e3) {
      return `${(num / 1e3).toFixed(1)}K`
    }
    return num.toFixed(1)
  }

  if (loading) {
    return <PageTemplate isLoading />
  }

  if (error || !portfolio) {
    return (
      <PageTemplate
        title="Portfolio Not Found"
        subtitle="The requested portfolio could not be loaded"
        maxWidth={CONTAINER_SIZES.WIDE}
      >
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="mx-auto text-muted-foreground mb-4" size={48} />
            <h3 className="text-lg font-semibold mb-2">Portfolio Not Found</h3>
            <p className="text-muted-foreground mb-4">
              {error || 'The portfolio you are looking for does not exist or you do not have access to it.'}
            </p>
            <Button
              variant="primary"
              onClick={() => navigate('/portfolio')}
            >
              Back to Portfolios
            </Button>
          </CardContent>
        </Card>
      </PageTemplate>
    )
  }

  const uniqueAssetIds = getUniqueAssetIds()
  const hasAssets = uniqueAssetIds.length > 0

  return (
    <PageTemplate maxWidth={CONTAINER_SIZES.WIDE}>
      <div className="space-y-6">
        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
          >
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </motion.div>
        )}

        {/* Page Header - aligned left as in Figma */}
        <div className="flex flex-col items-start">
          <h1 className="font-['Plus_Jakarta_Sans'] font-semibold leading-[40px] text-[#2d2923] dark:text-[#fdfdfd] text-[30px] tracking-[-0.28px] mb-2">
            {portfolioInfo?.name || 'Portfolio'}
          </h1>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[24px] text-[16px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
            Portfolio details and performance tracking
          </p>
        </div>

        {/* Metric Cards Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
        <MetricCard
          icon="invested"
          label="Total Invested"
          value={formatCompactNumber(portfolio.total_invested_amount)}
          valuePrefix="$ "
        />
        <MetricCard
          icon="current"
          label="Current Value"
          value={formatCompactNumber(portfolio.total_current_value)}
          valuePrefix="$ "
        />
        <MetricCard
          icon="roi"
          label="ROI"
          value={formatCompactNumber(portfolio.total_gain_loss_percentage || 0)}
          valueSuffix=" %"
        />
        <MetricCard
          icon="profit"
          label="Profit/Loss"
          value={formatCompactNumber(portfolio.total_gain_loss_usd || 0)}
          valuePrefix="$ "
        />
        <MetricCard
          icon="assets"
          label="Number of Assets"
          value={String(portfolio.total_assets)}
        />
      </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Portfolio Value vs Total Invested Chart */}
          <div className="bg-white dark:bg-[#121212] h-[465px] relative">
          <p className="absolute font-['Plus_Jakarta_Sans'] font-semibold leading-[24px] left-[32px] text-[#2d2923] dark:text-[#fdfdfd] text-[20px] top-[32px] tracking-[-0.2px]">
            Portfolio Value vs Total Invested
          </p>
          
          {chartLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : chartData.length > 0 ? (
            <>
              <div className="absolute left-[32px] right-[32px] top-[80px] bottom-[80px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="1 1" stroke="#e0e0e0" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(val) => {
                        const date = new Date(val)
                        return `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(2)}`
                      }}
                      tick={{ fontSize: 12, fill: '#878787' }}
                      tickMargin={10}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      tickFormatter={(value) => `$${formatCompactNumber(value)}`}
                      tick={{ fontSize: 16, fill: '#878787' }}
                      width={60}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip 
                      formatter={(value: number, name: string) => {
                        const labels: {[key: string]: string} = {
                          total_value: 'Portfolio Value',
                          total_invested: 'Total Invested',
                          gain_loss_usd: 'Profit/Loss'
                        }
                        return [formatMoney(value), labels[name] || name]
                      }}
                      labelFormatter={(label) => formatDate(label)}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="total_value" 
                      name="Portfolio Value"
                      stroke={CHART_COLORS.portfolioValue}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="total_invested" 
                      name="Total Invested"
                      stroke={CHART_COLORS.totalInvested}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="gain_loss_usd" 
                      name="Profit/Loss"
                      stroke={CHART_COLORS.profitLoss}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {/* Custom Legend */}
              <div className="absolute flex gap-[32px] items-center left-1/2 -translate-x-1/2 bottom-[24px]">
                <div className="flex gap-[12px] items-center">
                  <div className="bg-[#a9c804] h-[8px] w-[19px]" />
                  <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                    Portfolio Value
                  </p>
                </div>
                <div className="flex gap-[12px] items-center">
                  <div className="bg-[#ff8e00] h-[8px] w-[19px]" />
                  <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                    Total Invested
                  </p>
                </div>
                <div className="flex gap-[12px] items-center">
                  <div className="bg-[#2d2923] dark:bg-[#fdfdfd] h-[8px] w-[19px]" />
                  <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                    Profit/Loss
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              No chart data available
            </div>
          )}
        </div>

          {/* Asset Composition Over Time Chart */}
          <div className="bg-white dark:bg-[#121212] h-[465px] relative">
            <p className="absolute font-['Plus_Jakarta_Sans'] font-semibold leading-[24px] left-[32px] text-[#2d2923] dark:text-[#fdfdfd] text-[20px] top-[32px] tracking-[-0.2px]">
              Asset Composition Over Time
            </p>
          
          {chartLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : chartData.length > 0 && hasAssets ? (
            <>
              <div className="absolute left-[32px] right-[32px] top-[80px] bottom-[80px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="1 1" stroke="#e0e0e0" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(val) => {
                        const date = new Date(val)
                        return `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(2)}`
                      }}
                      tick={{ fontSize: 12, fill: '#878787' }}
                      tickMargin={10}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      tickFormatter={(value) => `$${formatCompactNumber(value)}`}
                      tick={{ fontSize: 16, fill: '#878787' }}
                      width={60}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => {
                        const assetIdMatch = name.match(/asset_value_(.+)/)
                        if (assetIdMatch && assetIdMatch[1]) {
                          const assetId = assetIdMatch[1]
                          const assetName = assetNames[assetId] || `Asset ${uniqueAssetIds.indexOf(assetId) + 1}`
                          return [formatMoney(value), assetName]
                        }
                        return [formatMoney(value), name]
                      }}
                      labelFormatter={(label) => formatDate(label)}
                    />
                    {uniqueAssetIds.slice(0, 5).map((assetId, index) => (
                      <Area
                        key={assetId}
                        type="monotone"
                        dataKey={(item: PortfolioGraphData['monthly_values'][0]) => {
                          const asset = item.assets?.find((a) => a.asset_id === assetId)
                          return asset ? asset.value : 0
                        }}
                        name={`asset_value_${assetId}`}
                        stackId="1"
                        stroke={STACKED_COLORS[index % STACKED_COLORS.length]}
                        fill={STACKED_COLORS[index % STACKED_COLORS.length]}
                        strokeWidth={1.5}
                        fillOpacity={0.7}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              {/* Custom Legend */}
              <div className="absolute flex gap-[32px] items-center left-1/2 -translate-x-1/2 bottom-[24px]">
                {uniqueAssetIds.slice(0, 3).map((assetId, index) => (
                  <div key={assetId} className="flex gap-[12px] items-center">
                    <div 
                      className="h-[8px] w-[19px]" 
                      style={{ backgroundColor: STACKED_COLORS[index % STACKED_COLORS.length] }}
                    />
                    <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-[14px]">
                      {assetNames[assetId] || `Asset ${index + 1}`}
                    </p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              No asset data available
            </div>
            )}
          </div>
        </div>

        {/* Portfolio Assets Section */}
        <div className="flex items-center justify-between w-full">
          <p className="font-['Plus_Jakarta_Sans'] font-semibold leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] text-[23px] tracking-[-0.2px]">
            Portfolio Assets
          </p>
          <div className="flex gap-2 items-center">
            <button
              onClick={handleExportPortfolio}
              className="bg-white dark:bg-[#121212] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] flex gap-[4px] items-center justify-center px-[16px] py-[8px] hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
            >
              <Download className="shrink-0 size-[16px] text-[#2d2923] dark:text-[#fdfdfd]" strokeWidth={1.5} />
              <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[16px] text-[#2d2923] dark:text-[#fdfdfd] text-[12px]">
                Download Template
              </p>
            </button>
            <button
              className="bg-[rgba(45,41,35,0.1)] dark:bg-[rgba(253,253,253,0.1)] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] flex gap-[4px] items-center justify-center px-[16px] py-[8px] hover:bg-[rgba(45,41,35,0.15)] dark:hover:bg-[rgba(253,253,253,0.15)] transition-colors"
            >
              <Upload className="shrink-0 size-[16px] text-[#2d2923] dark:text-[#fdfdfd]" strokeWidth={1.5} />
              <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[16px] text-[#2d2923] dark:text-[#fdfdfd] text-[12px]">
                Import CSV
              </p>
            </button>
            <button
              onClick={() => setShowAddAssetModal(true)}
              className="bg-[#2d2923] dark:bg-[#fdfdfd] flex gap-[4px] items-center justify-center px-[20px] py-[8px] hover:bg-[#3d3933] dark:hover:bg-[#e0e0e0] transition-colors"
            >
              <Plus className="shrink-0 size-[16px] text-white dark:text-[#2d2923]" strokeWidth={1.5} />
              <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[16px] text-white dark:text-[#2d2923] text-[12px]">
                Add Asset
              </p>
            </button>
          </div>
        </div>

        {/* Asset Cards Grid */}
        {!portfolio.assets || portfolio.assets.length === 0 ? (
          <div className="bg-white dark:bg-[#121212] p-12 text-center">
            <Building2 className="mx-auto text-muted-foreground mb-4" size={48} />
            <h3 className="text-lg font-semibold mb-2">No assets yet</h3>
            <p className="text-muted-foreground mb-4">
              Add assets to your portfolio to start tracking performance
            </p>
            <Button
              variant="primary"
              leftIcon={<Plus size={16} />}
              onClick={() => setShowAddAssetModal(true)}
            >
              Add Asset
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {portfolio.assets.map((asset) => (
              <AssetCard
                key={asset.asset_id}
                name={asset.project?.name || 'Unknown Project'}
                date={asset.date ? formatDate(asset.date) : '—'}
                investedAmount={formatMoney(asset.invested_amount)}
                valuation={asset.valuation ? formatMoney(asset.valuation) : '—'}
                terms={asset.terms || '—'}
                onViewDetails={() => navigate(`/portfolio/${portfolioId}/assets/${asset.asset_id}`)}
                onDelete={() => handleDeleteAsset(asset.asset_id)}
              />
            ))}
          </div>
        )}

        {/* Edit Portfolio Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>Edit Portfolio</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Portfolio Name</label>
                  <Input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    placeholder="Enter portfolio name"
                    onKeyPress={(e) => e.key === 'Enter' && handleEditPortfolio()}
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleEditPortfolio}
                    disabled={!editingName.trim()}
                    className="flex-1"
                  >
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Delete Portfolio Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>Delete Portfolio</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Are you sure you want to delete "{portfolioInfo?.name || 'this portfolio'}"? This action cannot be undone and will remove all associated assets.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDeletePortfolio}
                    disabled={deleting}
                    className="flex-1 text-red-500 border-red-500 hover:bg-red-500/10"
                  >
                    {deleting ? 'Deleting...' : 'Delete Portfolio'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <AddAssetModal
          isOpen={showAddAssetModal}
          onClose={() => setShowAddAssetModal(false)}
          portfolioId={portfolioId!}
          onSuccess={() => {
            loadData()
            loadChartData()
          }}
        />
      
        {editingAsset && (
          <EditAssetModal
            isOpen={showEditAssetModal}
            onClose={() => {
              setShowEditAssetModal(false)
              setEditingAsset(null)
            }}
            portfolioId={portfolioId!}
            assetId={editingAsset.asset_id}
            assetData={{
              project_name: editingAsset.project?.name,
              date: editingAsset.date || '',
              invested_amount: editingAsset.invested_amount,
              valuation: editingAsset.valuation,
              equity_or_tokens_amount: editingAsset.equity_or_tokens_amount,
              terms: editingAsset.terms || '',
              project_website: editingAsset.project_website,
              project_id: editingAsset.project?.project_id,
              project: editingAsset.project
            }}
            onSuccess={() => {
              loadData()
              loadChartData()
            }}
          />
        )}
      </div>
    </PageTemplate>
  )
}

export default PortfolioDetailScreen
